// ============================================================================
// WorkflowTimelinePanel — Case Lifecycle > Workflow Timeline
// ============================================================================
// Mirrors CaseClosurePanel + CaseHoldPanel structure:
//   1. Current-state card (state name, days-in-state, instance status)
//   2. Available actions (reuses <WorkflowActionBar>)
//   3. Audit history (reuses <WorkflowTimeline>)
//
// Replaces the previously-global "ACTIONS:" bar that consumed a full row at
// the top of the modal. Discoverability cost: one extra click via the
// sidebar item to advance the workflow.

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  WorkflowActionBar,
  WorkflowTimeline,
  useWorkflowInstance,
} from 'recsuit-workflow';
import type { LitigationCase } from 'api/types/litigationCase.types';

export interface WorkflowTimelinePanelProps {
  kase: LitigationCase;
  onMutated: () => void;
}

const WorkflowTimelinePanel: React.FC<WorkflowTimelinePanelProps> = ({ kase, onMutated }) => {
  const instanceId = kase.workflowInstanceId ?? '';
  const { data: instance, refetch: refetchInstance } = useWorkflowInstance(instanceId);

  const handleTriggered = React.useCallback(() => {
    void refetchInstance();
    onMutated();
  }, [refetchInstance, onMutated]);

  return (
    <div style={{ padding: '0.25rem 0' }}>
      <h3 style={headerStyle}>
        <IconSystem.clock size={18} style={{ marginRight: '0.45rem', verticalAlign: '-3px' }} />
        Workflow Timeline
      </h3>

      {/* Current state card */}
      {!instanceId ? (
        <div style={emptyCardStyle}>
          <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>Workflow not started</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
            Claim this case from the sidebar to start the workflow. Once started, available
            transitions and their audit history will appear here.
          </div>
        </div>
      ) : (
        <CurrentStateCard kase={kase} completedAt={instance?.instance?.completedAt ?? null} />
      )}

      {/* Available actions — reuses the WorkflowActionBar component */}
      {instanceId && !instance?.instance?.completedAt && (
        <div style={{
          marginTop: '0.85rem',
          padding: '0.45rem 0.5rem',
          border: '1px solid var(--border-color)',
          borderRadius: 4,
          background: 'var(--bg-primary)',
        }}>
          <WorkflowActionBar instanceId={instanceId} onTriggered={handleTriggered} />
        </div>
      )}

      {/* History timeline */}
      <h4 style={{ ...headerStyle, fontSize: '0.85rem', marginTop: '1.5rem' }}>History</h4>
      {instanceId ? (
        <WorkflowTimeline instanceId={instanceId} />
      ) : (
        <div style={mutedBlock}>No history — workflow not started yet.</div>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------

const CurrentStateCard: React.FC<{
  kase: LitigationCase;
  completedAt: string | null;
}> = ({ kase, completedAt }) => {
  const isCompleted = !!completedAt;
  const stateName = kase.currentStateDisplayName ?? kase.currentStateCode ?? 'Unknown';
  const days = kase.daysInCurrentState ?? 0;

  return (
    <div style={{
      padding: '0.85rem 1rem',
      border: '1px solid var(--border-color)',
      borderLeft: isCompleted ? '4px solid #6b7280' : '4px solid #2563eb',
      borderRadius: 6,
      background: 'var(--bg-primary)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)' }}>
          Current State
        </span>
        <strong style={{ fontSize: '0.95rem' }}>{stateName}</strong>
        {isCompleted && (
          <span style={{
            padding: '1px 8px', borderRadius: '999px',
            fontSize: '0.65rem', fontWeight: 600,
            background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
          }}>Frozen (case completed)</span>
        )}
      </div>
      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
        {kase.workflowDefinitionCode ?? '—'}
        {' · '}Day {days} in this state
        {kase.stateEnteredAt && (
          <> · Entered {new Date(kase.stateEnteredAt).toLocaleDateString('en-IN')}</>
        )}
      </div>
    </div>
  );
};

const headerStyle: React.CSSProperties = {
  fontSize: '1rem', fontWeight: 600, margin: '0 0 0.6rem',
  paddingBottom: '0.3rem', borderBottom: '1px solid var(--border-color)',
};

const emptyCardStyle: React.CSSProperties = {
  padding: '0.85rem 1rem',
  border: '1px dashed var(--form-border-color)',
  borderRadius: 6,
  background: 'var(--bg-secondary)',
};

const mutedBlock: React.CSSProperties = {
  padding: '0.65rem 0.85rem',
  fontSize: '0.78rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};

export default WorkflowTimelinePanel;
